import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { apiService } from '../../../services';
import { ApiResponse } from '../../../types/api';

interface AgendamentoConfig {
  ativo: boolean;
  dias_semana: string[];
  horarios: string[];
}

interface WhatsAppConfig {
  numero_padrao: string;
  conexao_ativa: boolean;
  conexoes_selecionadas: number[];
}

interface ChecklistPersonalizado {
  id?: number;
  veiculo_id: number;
  ativo: boolean;
  grupos: any[];
  agendamento_automatico: AgendamentoConfig;
  whatsapp_config: WhatsAppConfig;
}

const VehicleAgendamentosView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [checklist, setChecklist] = useState<ChecklistPersonalizado | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const diasSemana = [
    { value: 'segunda', label: 'Segunda-feira' },
    { value: 'terca', label: 'Terça-feira' },
    { value: 'quarta', label: 'Quarta-feira' },
    { value: 'quinta', label: 'Quinta-feira' },
    { value: 'sexta', label: 'Sexta-feira' },
    { value: 'sabado', label: 'Sábado' },
    { value: 'domingo', label: 'Domingo' }
  ];

  const loadChecklist = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Verificar se o ID é válido (não NaN ou undefined)
      const vehicleId = Number(id);
      if (isNaN(vehicleId) || !id) {
        // Para criação de veículo, usar estado vazio
        setChecklist({
          veiculo_id: 0, // ID temporário para criação
          ativo: false,
          grupos: [],
          agendamento_automatico: {
            ativo: false,
            dias_semana: [],
            horarios: []
          },
          whatsapp_config: {
            numero_padrao: '',
            conexao_ativa: false,
            conexoes_selecionadas: []
          }
        });
        setIsLoading(false);
        return;
      }
      
      const response = await apiService.getVeiculoChecklistAdaptadoByVehicle(vehicleId) as ApiResponse<any>;
      
      if (response.success && response.data) {
        setChecklist(response.data);
      } else {
        // Se não existe checklist, criar um padrão vazio
        setChecklist({
          veiculo_id: vehicleId,
          ativo: false,
          grupos: [],
          agendamento_automatico: {
            ativo: false,
            dias_semana: [],
            horarios: []
          },
          whatsapp_config: {
            numero_padrao: '',
            conexao_ativa: false,
            conexoes_selecionadas: []
          }
        });
      }
    } catch (error) {
      console.error('Erro ao carregar checklist:', error);
      setError('Erro ao carregar configurações de agendamento');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadChecklist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (isLoading) {
    return (
      <div className="card">
        <div className="card-body" style={{ textAlign: 'center', padding: '50px 20px' }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', color: '#04C64F', marginBottom: '20px' }}></i>
          <p>Carregando configurações de agendamento...</p>
        </div>
      </div>
    );
  }

  if (error || !checklist) {
    return (
      <div className="card">
        <div className="card-body" style={{ textAlign: 'center', padding: '50px 20px' }}>
          <i className="fas fa-exclamation-triangle" style={{ fontSize: '2rem', color: '#e74c3c', marginBottom: '20px' }}></i>
          <p>Erro ao carregar configurações de agendamento.</p>
          <button className="btn btn-primary" onClick={loadChecklist}>
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Configuração de Agendamento Automático */}
      <div className="card">
        <div className="card-header">
          <h3 style={{ margin: 0, color: '#2c3e50' }}>
            <i className="fas fa-clock"></i>
            Agendamento Automático de Checklists
          </h3>
        </div>
        <div className="card-body">
          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <div className="info-item">
              <label className="form-label">Status do Agendamento</label>
              <span className="info-value">
                <span className={`status-badge ${checklist.agendamento_automatico.ativo ? 'status-active' : 'status-inactive'}`}>
                  {checklist.agendamento_automatico.ativo ? 'Ativo' : 'Inativo'}
                </span>
              </span>
            </div>

            {checklist.agendamento_automatico.ativo && (
              <>
                <div className="info-item">
                  <label className="form-label">Dias da Semana</label>
                  <span className="info-value">
                    {checklist.agendamento_automatico.dias_semana.length > 0 
                      ? checklist.agendamento_automatico.dias_semana
                          .map(dia => diasSemana.find(d => d.value === dia)?.label)
                          .join(', ')
                      : 'Nenhum dia selecionado'}
                  </span>
                </div>

                <div className="info-item">
                  <label className="form-label">Horários de Envio</label>
                  <span className="info-value">
                    {checklist.agendamento_automatico.horarios.length > 0 
                      ? checklist.agendamento_automatico.horarios.join(', ')
                      : 'Nenhum horário selecionado'}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Configuração WhatsApp */}
      <div className="card" style={{ marginTop: '20px' }}>
        <div className="card-header">
          <h3 style={{ margin: 0, color: '#2c3e50' }}>
            <i className="fab fa-whatsapp"></i>
            Configuração WhatsApp
          </h3>
        </div>
        <div className="card-body">
          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <div className="info-item">
              <label className="form-label">Número WhatsApp Padrão</label>
              <span className="info-value">
                {checklist.whatsapp_config.numero_padrao || 'Não configurado'}
              </span>
            </div>

            <div className="info-item">
              <label className="form-label">Status WhatsApp</label>
              <span className="info-value">
                <span className={`status-badge ${checklist.whatsapp_config.conexao_ativa ? 'status-active' : 'status-inactive'}`}>
                  {checklist.whatsapp_config.conexao_ativa ? 'Ativo' : 'Inativo'}
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Resumo da Configuração */}
      {checklist.agendamento_automatico.ativo && (
        <div className="card" style={{ marginTop: '20px', backgroundColor: '#f8f9fa' }}>
          <div className="card-header">
            <h4 style={{ margin: 0, color: '#2c3e50' }}>
              <i className="fas fa-info-circle"></i>
              Resumo da Configuração
            </h4>
          </div>
          <div className="card-body">
            <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}>
              <div className="info-item">
                <label className="form-label">Status do Agendamento</label>
                <span className="info-value">
                  <span className="status-badge status-active">Ativo</span>
                </span>
              </div>

              <div className="info-item">
                <label className="form-label">Dias Selecionados</label>
                <span className="info-value">
                  {checklist.agendamento_automatico.dias_semana.length > 0 
                    ? checklist.agendamento_automatico.dias_semana
                        .map(dia => diasSemana.find(d => d.value === dia)?.label)
                        .join(', ')
                    : 'Nenhum dia selecionado'}
                </span>
              </div>

              <div className="info-item">
                <label className="form-label">Horários Selecionados</label>
                <span className="info-value">
                  {checklist.agendamento_automatico.horarios.length > 0 
                    ? checklist.agendamento_automatico.horarios.join(', ')
                    : 'Nenhum horário selecionado'}
                </span>
              </div>

              <div className="info-item">
                <label className="form-label">Número WhatsApp</label>
                <span className="info-value">
                  {checklist.whatsapp_config.numero_padrao || 'Não configurado'}
                </span>
              </div>

              <div className="info-item">
                <label className="form-label">Status WhatsApp</label>
                <span className="info-value">
                  <span className={`status-badge ${checklist.whatsapp_config.conexao_ativa ? 'status-active' : 'status-inactive'}`}>
                    {checklist.whatsapp_config.conexao_ativa ? 'Ativo' : 'Inativo'}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {!checklist.agendamento_automatico.ativo && (
        <div className="card" style={{ marginTop: '20px', backgroundColor: '#f8f9fa' }}>
          <div className="card-body" style={{ textAlign: 'center', padding: '30px 20px' }}>
            <i className="fas fa-clock" style={{ fontSize: '3rem', color: '#95a5a6', marginBottom: '15px' }}></i>
            <h4 style={{ color: '#7f8c8d', marginBottom: '10px' }}>Agendamento Desativado</h4>
            <p style={{ color: '#95a5a6' }}>
              O agendamento automático de checklists está desativado para este veículo.
              Para configurar, edite o veículo e acesse a aba "Agendamentos".
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleAgendamentosView;
